import { useState } from "react";
import { Container, Form, Button, Stack } from "react-bootstrap";
import { LuPencil } from "react-icons/lu";
import Flag from "./Flag";
import catalogo from "../../assets/catalogo.json";
import {
  validateNombre,
  validateEstado,
  validateDepartamento,
  validateEmail,
  validateTelefono,
} from "../../validations/solicitanteValidation";
import {
  formatRut,
  formatTelefono,
  formatNombre,
} from "../../validations/format";
import { editarSolicitante } from "../../services/apis_funcionarios";

const styles = {
  container: {
    backgroundColor: "var(--color-background-3)",
    height: "100%",
    width: "100%",
  },
};

function DatosSolicitante({
  idS,
  nombre,
  tipo,
  carrera,
  rut,
  isPasaporte = false,
  email,
  telefono,
  flag,
  isNew,
}) {
  const [isEditing, setIsEditing] = useState(isNew);
  const [hasData, setHasData] = useState(!isNew);
  console.log("pasaporte:", isPasaporte);
  console.log("RUT:", rut);
  const initialData = {
    nombre: nombre || "",
    tipo: tipo || "",
    carrera: carrera || "",
    email: email || "",
    telefono: telefono ? formatTelefono(telefono) : "",
  };

  const [data, setData] = useState(initialData);
  const [backupData, setBackupData] = useState(initialData); // respaldo

  const [flagState, setFlagState] = useState(flag);
  const [errors, setErrors] = useState({});

  // Centraliza todas las validaciones
  const validateField = (name, value) => {
    switch (name) {
      case "nombre":
        return validateNombre(value);
      case "tipo":
        return validateEstado(value);
      case "carrera":
        return validateDepartamento(value);
      case "email":
        return validateEmail(value);
      case "telefono":
        return validateTelefono(value);
      default:
        return "";
    }
  };

  // Actualiza el valor sin validar todavía
  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  // Valida (y formatea si aplica) al salir del campo
  const handleBlur = (e) => {
    const { name, value } = e.target;
    const formattedValue = name === "telefono" ? formatTelefono(value) : value;

    const errorMsg = validateField(name, formattedValue);

    setData((prev) => ({ ...prev, [name]: formattedValue }));
    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  // Guardar cambios
  const handleSubmit = async (e) => {
    e.preventDefault();

    const formattedTelefono = formatTelefono(data.telefono);

    const newErrors = {
      nombre: validateNombre(data.nombre),
      tipo: validateEstado(data.tipo),
      carrera: validateDepartamento(data.carrera),
      email: validateEmail(data.email),
      telefono: validateTelefono(formattedTelefono),
    };

    setErrors(newErrors);
    const hasErrors = Object.values(newErrors).some((msg) => msg);
    if (hasErrors) return;

    const payload = {
      tipo_estudiante: data.tipo,
      nombre: data.nombre,
      carrera: data.carrera,
      email: data.email,
      telefono: formattedTelefono || null,
    };

    console.log("Datos enviados:", payload);

    try {
      const mensaje = await editarSolicitante(idS, payload);
      // Solo si sale bien:
      setBackupData(data); // Actualiza respaldo
      setIsEditing(false);
      setHasData(true);
    } catch (error) {
      setIsEditing(true);
    }
  };

  // Cancelar edición
  const handleCancel = () => {
    setData(backupData); // restaura el estado previo
    setErrors({});
    setIsEditing(false);
  };

  return (
    <Container fluid className="rounded p-4" style={styles.container}>
      {!isEditing ? (
        <Stack>
          <Stack direction="horizontal" className="justify-content-between">
            <h1 className="fs-4 mb-0">{data.nombre}</h1>

            <Flag
              idSolicitante={idS}
              initialFlag={flagState}
              onChange={(nuevoFlag) => setFlagState(nuevoFlag)}
              size={35}
            />
          </Stack>

          <p>
            <strong>{isPasaporte ? "Pasaporte" : "RUT"}:</strong>{" "}
            {isPasaporte ? rut : formatRut(rut)}</p>
          <p>
            {data.tipo} - {data.carrera}
          </p>
          <hr />
          <p>Email: {data.email}</p>
          {data.telefono && <p>Teléfono: {data.telefono}</p>}

          <Button
            size="sm"
            className="ms-auto btn-primary-blue"
            onClick={() => {
              setBackupData(data); // guarda copia antes de editar
              setIsEditing(true);
            }}
          >
            <LuPencil className="me-1" />
            Editar
          </Button>
        </Stack>
      ) : (
        <Form>
          {/* Nombre */}
          <Form.Group className="mb-2">
            <Form.Label>Nombre</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              name="nombre"
              value={data.nombre}
              onChange={(e) => {
                const formatted = formatNombre(e.target.value);
                setData((prev) => ({ ...prev, nombre: formatted }));
              }}
              onBlur={handleBlur}
              isInvalid={!!errors.nombre}
            />
            <Form.Control.Feedback type="invalid">
              {errors.nombre}
            </Form.Control.Feedback>
          </Form.Group>

          {/* RUT o Pasaporte */}
          <Form.Group className="mb-2">
            <Form.Label>{isPasaporte ? "Pasaporte" : "RUT"}</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              value={isPasaporte ? rut || "" : formatRut(rut || "")}
              readOnly
              />
          </Form.Group>

          {/* Tipo */}
          <Form.Group className="mb-2">
            <Form.Label>Estado</Form.Label>
            <Form.Select
              size="sm"
              name="tipo"
              value={data.tipo}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={!!errors.tipo}
            >
              <option value="">Seleccione estado...</option>
              {catalogo.estados.map((estado) => (
                <option key={estado} value={estado}>
                  {estado}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.tipo}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Carrera */}
          <Form.Group className="mb-2">
            <Form.Label>Departamento</Form.Label>
            <Form.Select
              size="sm"
              name="carrera"
              value={data.carrera}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={!!errors.carrera}
            >
              <option value="">Seleccione departamento...</option>
              {catalogo.departamentos.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.carrera}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Email */}
          <Form.Group className="mb-2">
            <Form.Label>Email</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              name="email"
              value={data.email}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={!!errors.email}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Teléfono */}
          <Form.Group className="mb-2">
            <Form.Label>Teléfono</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              name="telefono"
              value={data.telefono}
              onChange={(e) => {
                const formatted = formatTelefono(e.target.value);
                setData((prev) => ({ ...prev, telefono: formatted }));
              }}
              onBlur={handleBlur}
              placeholder="+56 9 XXXX XXXX"
              isInvalid={!!errors.telefono}
            />
            <Form.Control.Feedback type="invalid">
              {errors.telefono}
            </Form.Control.Feedback>
          </Form.Group>

          <Stack direction="horizontal" gap={2} className="mt-4">
            {hasData && (
              <Button size="sm" variant="secondary" onClick={handleCancel}>
                Cancelar
              </Button>
            )}
            <Button
              size="sm"
              className="ms-lg-auto btn-primary-blue"
              onClick={handleSubmit}
            >
              Guardar
            </Button>
          </Stack>
        </Form>
      )}
    </Container>
  );
}

export default DatosSolicitante;
